/** Shared media utilities used across Generate, Videos, and Edit pages. */

import { toast } from "@/lib/toast";
import { getCurrentTranslations } from "@/lib/i18n/translations";
import { SITE_URL } from "@/config/constants";

/**
 * Detect the closest standard aspect ratio from image dimensions.
 */
export function detectAspectRatio(width: number, height: number): string {
  const ratio = width / height;
  const ratios = [
    { id: "21:9", value: 21 / 9 },
    { id: "16:9", value: 16 / 9 },
    { id: "4:3", value: 4 / 3 },
    { id: "3:2", value: 3 / 2 },
    { id: "5:4", value: 5 / 4 },
    { id: "1:1", value: 1 },
    { id: "4:5", value: 4 / 5 },
    { id: "3:4", value: 3 / 4 },
    { id: "2:3", value: 2 / 3 },
    { id: "9:16", value: 9 / 16 },
  ];
  let closest = ratios[0];
  let minDiff = Math.abs(ratio - ratios[0].value);
  for (const r of ratios) {
    const diff = Math.abs(ratio - r.value);
    if (diff < minDiff) {
      minDiff = diff;
      closest = r;
    }
  }
  return closest.id;
}

/** Check if a string is a URL (not base64) */
export function isUrl(value: string): boolean {
  return value.startsWith("http://") || value.startsWith("https://");
}

const MIME_TYPES: Record<string, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
  gif: "image/gif",
  mp4: "video/mp4",
};

function getMimeType(extension: string): string {
  return MIME_TYPES[extension] ?? "application/octet-stream";
}

/**
 * Download a media file (image or video) by fetching the blob
 * and triggering a browser download.
 *
 * On mobile, uses the Web Share API (files) for reliable saving.
 * Falls back to an anchor-click approach on desktop.
 */
export async function downloadMedia(
  url: string,
  filenamePrefix: string,
  extension = "png",
) {
  const t = getCurrentTranslations();
  try {
    const response = await fetch(url, { mode: "cors", cache: "no-store" });
    if (!response.ok) throw new Error(`Failed to download ${url}: ${response.status}`);
    const blob = await response.blob();
    const filename = `${filenamePrefix}-${Date.now()}.${extension}`;

    // On mobile, use Web Share API with files for reliable downloads
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile && navigator.canShare) {
      const file = new File([blob], filename, { type: getMimeType(extension) });
      if (navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file] });
          toast.success(t.common.downloadSuccess);
          return;
        } catch (shareError) {
          // User cancelled the share sheet — exit silently
          if (shareError instanceof Error && shareError.name === "AbortError") return;
          // Other share errors: fall through to anchor approach
        }
      }
    }

    // Desktop / fallback: anchor element download
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // Delayed cleanup so the browser has time to process the download
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    }, 1500);

    toast.success(t.common.downloadSuccess);
  } catch (error) {
    console.error(`Error downloading ${extension}:`, error);
    toast.error(t.common.downloadError);
  }
}

export type ShareMediaType = "image" | "video";

export interface ShareTarget {
  /** UUID of the row in generated_images or generated_videos. */
  id: string;
  /** Discriminator — picked up by the /s/:id page to open the right lightbox. */
  type: ShareMediaType;
}

/**
 * Build the canonical branded share URL for a generation.
 * Exported for places that need the URL itself (e.g. the lightbox's Share button
 * tooltip, or copy-to-clipboard surfaces that don't go through `shareMedia`).
 */
export function buildShareUrl(target: ShareTarget): string {
  return `${SITE_URL}/s/${target.id}`;
}

/**
 * Share a generated image or video via a branded in-house URL (e.g.
 * `https://app.todowith.ai/s/<uuid>`). Opening that URL drops the recipient
 * inside the app and pops the same lightbox the creator sees — preserving
 * brand context instead of leaking a raw CDN file.
 *
 * Mobile / supported browsers: native share sheet via `navigator.share({ url })`.
 * Desktop fallback: copies the URL to the clipboard and shows a confirmation toast.
 * User-cancelled share sheets exit silently.
 */
export async function shareMedia(target: ShareTarget): Promise<void> {
  const t = getCurrentTranslations();
  if (!target?.id) return;

  const shareUrl = buildShareUrl(target);

  if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
    try {
      await navigator.share({ url: shareUrl });
      return;
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return;
      // Other share failures fall through to the clipboard fallback.
    }
  }

  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t.common.linkCopied);
      return;
    }
    throw new Error("Clipboard unavailable");
  } catch (err) {
    console.error("Error sharing media:", err);
    toast.error(t.common.shareError);
  }
}
